// Review and publish (Lane 1331, Phase 2 slice 2A). The server says what
// blocks publishing and what only needs a look, and builds the preview with
// the same code as the supplier portal, so the buyer checks exactly what each
// supplier will read. Publishing freezes the parts and opens round 1.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { STALE_MESSAGE, endSentence, newKey } from './sourcingApi';
import InvitationLinks, { DELIVERY_TEXT } from './InvitationLinks';
import { viewerZone } from './useSourcingChat';
import { EventHeader, EventDetails, PartsTable, formatDueAt } from '../sourcing-portal/portalView';

const MIN_LEAD_MS = 5 * 60 * 1000;

// Lane 1438: what the buyer typed here is saved as they leave each field, so closing
// the panel or reloading never loses it.
function pad(n) { return String(n).padStart(2, '0'); }

// A time as the datetime-local field shows it, in this browser's zone.
function toLocalInput(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// What the server has saved, as the fields show it. A contact never saved takes the
// supplier record's email; one the buyer cleared stays empty.
export function savedValues(review) {
  const draft = (review && review.draft) || {};
  let due = '';
  if (draft.due_at) {
    const d = new Date(draft.due_at);
    if (!Number.isNaN(d.getTime())) due = toLocalInput(d);
  } else if (draft.due_date || (review && review.proposed_due_date)) {
    due = `${String(draft.due_date || review.proposed_due_date).slice(0, 10)}T17:00`;
  }
  const contacts = {};
  ((review && review.suppliers) || []).forEach((s) => {
    const email = s.contact_email !== null && s.contact_email !== undefined ? s.contact_email : (s.master_email || '');
    contacts[s.supplier_id] = { email: email || '', name: s.contact_name || '' };
  });
  return { title: draft.supplier_title || '', due, contacts };
}

const SAVE_TEXT = { saving: 'Saving\u2026', saved: 'Saved' };

function sampleText(item) {
  if (!item || typeof item !== 'object') return String(item);
  if (Array.isArray(item.units)) return `${item.material_number}: ${item.units.join(', ')}`;
  return item.site ? `${item.material_number} at ${item.site}` : String(item.material_number || '');
}

export function CheckList({ items, tone, names = {} }) {
  if (!items || items.length === 0) return null;
  return (
    <ul className={`srce-checks srce-checks--${tone}`}>
      {items.map((b, i) => (
        <li key={`${b.code}-${b.supplier_id || ''}-${i}`}>
          {b.message}
          {b.supplier_id && names[b.supplier_id] && !String(b.message).includes(names[b.supplier_id])
            ? ` (${names[b.supplier_id]})` : ''}
          {Array.isArray(b.sample) && b.sample.length > 0 && (
            <div className="srce-checks-sample">
              {b.sample.map(sampleText).join('; ')}
              {b.count > b.sample.length ? `; and ${(b.count - b.sample.length).toLocaleString()} more` : ''}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

export default function PublishReview({ api, eventId, onClose, onPublished, onDone, onSaved }) {
  const [title, setTitle] = useState('');
  const [dueLocal, setDueLocal] = useState('');
  const [shareSites, setShareSites] = useState(false);
  const [contacts, setContacts] = useState({});
  const [review, setReview] = useState(null);
  const [reviewError, setReviewError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishBlocks, setPublishBlocks] = useState(null);
  const [publishError, setPublishError] = useState('');
  const [result, setResult] = useState(null);
  const requestId = useRef(0);
  const firstLoad = useRef(true);
  const [reloadTick, setReloadTick] = useState(0);
  const [previewPage, setPreviewPage] = useState(1);
  // Lane 1438: saving as the buyer leaves a field. `touched`: fields typed in since
  // their last save (a re-read never overwrites them); `revision`: the revision the
  // buyer reviewed, which publish sends (our own saves move it by one each).
  const [saveState, setSaveState] = useState('');      // '' | saving | saved
  const [saveError, setSaveError] = useState('');
  const [closeFailed, setCloseFailed] = useState(false);
  const [closing, setClosing] = useState(false);
  const touched = useRef(new Set());
  const lastSave = useRef(new Map());                   // field -> the number of its newest save
  const busy = useRef(false);                           // closing or publishing
  const failed = useRef(new Map());                     // field -> the save to retry
  const queue = useRef(Promise.resolve());
  const pending = useRef(0);
  const saveSeq = useRef(0);
  const revision = useRef(null);
  const live = useRef({ title: '', due: '', contacts: {} });
  live.current = { title, due: dueLocal, contacts };

  // The fields untouched since their last save take the saved values: a change made
  // in another window shows here, and publish never sends a value nobody sees.
  const reconcile = useCallback((r) => {
    const saved = savedValues(r);
    if (!touched.current.has('title')) setTitle(saved.title);
    if (!touched.current.has('due')) setDueLocal(saved.due);
    setContacts((c) => {
      const next = { ...c };
      Object.entries(saved.contacts).forEach(([sid, v]) => {
        if (!touched.current.has(`contact:${sid}`)) next[sid] = v;
      });
      return next;
    });
  }, []);

  // Re-read the review when the title or site sharing changes (after a short
  // pause in typing): both change the warnings and the preview.
  useEffect(() => {
    const id = requestId.current + 1;
    requestId.current = id;
    const run = () => {
      setRefreshing(true);
      const seq = saveSeq.current;
      api.publishReview(eventId, { supplier_title: title.trim(), share_sites: shareSites, preview_page: previewPage })
        .then((r) => {
          if (requestId.current !== id) return;
          setReview(r);
          setReviewError('');
          // While closing or publishing, a background read changes nothing on screen:
          // publish reads the draft itself and stops when anything differs.
          if (busy.current) return;
          // A save that started or ended while this was read makes it older than the
          // saved draft: read again instead of taking its values.
          if (seq !== saveSeq.current || pending.current > 0) { setReloadTick((n) => n + 1); return; }
          revision.current = r.revision;
          reconcile(r);
        })
        .catch((e) => { if (requestId.current === id) setReviewError(e.message); })
        .finally(() => { if (requestId.current === id) setRefreshing(false); });
    };
    const delay = firstLoad.current ? 0 : 400;
    firstLoad.current = false;
    const t = setTimeout(run, delay);
    return () => clearTimeout(t);
  }, [api, eventId, title, shareSites, reloadTick, previewPage, reconcile]);

  // The save body for one field from what is on screen now, and the value it saves
  // (to tell whether the buyer typed again while it was saving). None: nothing whole to
  // save yet (a date without a time).
  const bodyFor = (field) => {
    const now = live.current;
    if (field === 'title') return { body: { supplier_title: now.title.trim() }, value: now.title };
    if (field === 'due') {
      if (!now.due) return { body: { due_at: '' }, value: '' };
      const d = new Date(now.due);
      if (Number.isNaN(d.getTime())) return null;
      return { body: { due_at: d.toISOString(), ...(viewerZone() ? { time_zone: viewerZone() } : {}) }, value: now.due };
    }
    const supplierId = Number(field.slice('contact:'.length));
    const c = now.contacts[supplierId] || {};
    return {
      body: { contacts: [{ supplier_id: supplierId, email: (c.email || '').trim(), name: (c.name || '').trim() }] },
      value: `${c.email || ''}\n${c.name || ''}`,
    };
  };

  // One save at a time, in the order the buyer left the fields.
  const enqueue = (field, body, value, key) => {
    pending.current += 1;
    saveSeq.current += 1;
    const mine = (lastSave.current.get(field) || 0) + 1;
    lastSave.current.set(field, mine);
    setSaveState('saving');
    const run = async () => {
      try {
        const out = await api.savePublishDraft(eventId, body, key);
        // Saved: the field is clean only when this was its newest save and the screen
        // still shows what it saved (else a later save or the buyer's typing is newer).
        if (lastSave.current.get(field) === mine) {
          failed.current.delete(field);
          const now = bodyFor(field);
          if (now && now.value === value) touched.current.delete(field);
        }
        const skipped = ((out && out.result) || {}).skipped_suppliers || [];
        if (revision.current !== null && out.revision === revision.current + 1 && skipped.length === 0) {
          revision.current = out.revision;
        } else {
          setReloadTick((n) => n + 1);   // someone else changed the event: read it again
        }
        if (onSaved) onSaved();
      } catch (e) {
        // Kept with its key: a retry of a save whose answer was lost is never applied twice.
        failed.current.set(field, { key, value });
        setSaveError(e.message || 'Try again.');
      } finally {
        pending.current -= 1;
        saveSeq.current += 1;
        if (pending.current === 0) {
          setSaveState(failed.current.size ? '' : 'saved');
          if (!failed.current.size) setSaveError('');
        }
      }
    };
    queue.current = queue.current.then(run);
    return queue.current;
  };

  // Save one field when the buyer leaves it (or before publish and close).
  const saveField = (field) => {
    if (!touched.current.has(field)) return;
    const spec = bodyFor(field);
    if (!spec) return;
    const prior = failed.current.get(field);
    enqueue(field, spec.body, spec.value, prior && prior.value === spec.value ? prior.key : newKey());
  };

  // Every save done, then every field still typed in saved; true when nothing typed is
  // left unsaved. The fields are locked while this runs (closing or publishing), so no
  // new typing can slip in after the last pass.
  const flush = async () => {
    for (let pass = 0; pass < 3; pass += 1) {
      await queue.current;
      const left = Array.from(touched.current).filter((f) => bodyFor(f));
      // The first pass also retries a save that failed earlier (with its own key).
      if (left.length === 0 || (pass > 0 && failed.current.size)) break;
      left.forEach(saveField);
    }
    await queue.current;
    return failed.current.size === 0 && Array.from(touched.current).every((f) => !bodyFor(f));
  };

  const close = async () => {
    if (closeFailed) { onClose(); return; }
    setClosing(true);
    busy.current = true;
    try {
      if (await flush()) { onClose(); return; }
      setCloseFailed(true);
    } finally {
      busy.current = false;
      setClosing(false);
    }
  };
  const locked = closing || publishing;

  const suppliers = (review && review.suppliers) || [];
  const names = Object.fromEntries(suppliers.map((s) => [s.supplier_id, s.supplier_name]));
  const dueDate = dueLocal ? new Date(dueLocal) : null;
  const dueValid = dueDate && !Number.isNaN(dueDate.getTime());
  const missing = [];
  if (!title.trim()) missing.push('Enter the title suppliers see.');
  if (!dueLocal) missing.push('Choose when offers are due.');
  else if (!dueValid || dueDate.getTime() <= Date.now() + MIN_LEAD_MS) missing.push('The due time must be at least 5 minutes from now.');
  suppliers.forEach((s) => {
    if (!((contacts[s.supplier_id] || {}).email || '').trim()) missing.push(endSentence(`Enter a contact email for ${s.supplier_name}`));
  });
  const blocked = !review || review.blocks.length > 0;

  const setContact = (supplierId, field, value) => {
    touched.current.add(`contact:${supplierId}`);
    setSaveState('');
    setContacts((c) => ({ ...c, [supplierId]: { ...(c[supplierId] || { email: '', name: '' }), [field]: value } }));
  };

  const publish = async () => {
    setPublishing(true);
    busy.current = true;
    setPublishBlocks(null);
    setPublishError('');
    try {
      // Everything typed is saved first; then the saved draft is read once more, and
      // anything another window changed is shown before anything is sent.
      if (!(await flush())) {
        setPublishError('Some details are not saved. Check your connection and try again.');
        return;
      }
      const fresh = await api.publishReview(eventId, { supplier_title: live.current.title.trim(),
        share_sites: shareSites, preview_page: previewPage });
      const saved = savedValues(fresh);
      // The fields are locked while publishing: this is exactly what is on screen and saved.
      const screen = live.current;
      const differs = saved.title.trim() !== screen.title.trim() || saved.due !== screen.due
        || Object.entries(saved.contacts).some(([sid, v]) => {
          const c = screen.contacts[sid] || {};
          return (v.email || '').trim() !== (c.email || '').trim() || (v.name || '').trim() !== (c.name || '').trim();
        });
      if (differs || fresh.revision !== revision.current) {
        setReview(fresh);
        revision.current = fresh.revision;
        touched.current.clear();
        reconcile(fresh);
        setPublishError('This event changed since you opened this panel. Check the details below, then publish.');
        return;
      }
      const r = await api.publish(eventId, {
        supplier_title: screen.title.trim(),
        due_at: new Date(screen.due).toISOString(),
        // The event's time zone: notices and emails name due times in it (Lane 1393).
        ...(viewerZone() ? { time_zone: viewerZone() } : {}),
        share_sites: shareSites,
        contacts: (fresh.suppliers || []).map((s) => {
          const c = screen.contacts[s.supplier_id] || {};
          const name = (c.name || '').trim();
          return { supplier_id: s.supplier_id, email: (c.email || '').trim(), ...(name ? { name } : {}) };
        }),
      }, revision.current);
      setResult(r);
      await onPublished();
    } catch (e) {
      if (e.code === 'publish_blocked') {
        setPublishBlocks((e.detail && e.detail.blocks) || []);
        setPublishError(e.message);
      } else if (e.code === 'stale_revision') {
        setPublishError(STALE_MESSAGE);
        setReloadTick((n) => n + 1);
        await onPublished();
      } else {
        setPublishError(e.message);
      }
    } finally {
      busy.current = false;
      setPublishing(false);
    }
  };

  if (result) {
    const inv = (result.result && result.result.invitations) || [];
    const delivery = result.delivery || {};
    const invNames = Object.fromEntries(inv.map((i) => [i.supplier_id, i.supplier_name]));
    const due = formatDueAt(result.result && result.result.due_at);
    return (
      <section className="srce-panel" aria-label="Published">
        <h2 className="srce-h2 srce-h2--flush">Published</h2>
        <div className="srce-good">Round 1 is open{due ? ` until ${due}` : ''}. The parts in this event can no longer change.</div>
        {result.links && result.links.length > 0 ? (
          <InvitationLinks links={result.links} names={invNames} />
        ) : (
          <ul className="srce-plainlist">
            {inv.map((i) => (
              <li key={i.invitation_id}>
                <span className="srce-strong">{i.supplier_name}</span>, {i.email}:{' '}
                {DELIVERY_TEXT[delivery[i.invitation_id] || i.delivery] || 'Invitation saved'}
              </li>
            ))}
          </ul>
        )}
        <div className="srce-actions">
          <button type="button" className="srce-btn srce-btn--primary" onClick={() => onDone('invitations')}>Go to invitations</button>
        </div>
      </section>
    );
  }

  const preview = review && review.preview;
  return (
    <section className="srce-panel" aria-label="Review and publish">
      <div className="srce-actions">
        <h2 className="srce-h2 srce-h2--flush">Review and publish</h2>
        {refreshing && <span className="srce-mut">Checking…</span>}
        {saveState && !saveError && <span className="srce-mut" role="status">{SAVE_TEXT[saveState]}</span>}
        <button type="button" className="ll-clr srce-push" disabled={closing} onClick={close}>{closeFailed ? 'Close without saving' : 'Close'}</button>
      </div>
      {saveError && <div className="srce-error" role="alert">Not saved: {saveError}</div>}
      {reviewError && <div className="srce-error" role="alert">{reviewError}</div>}
      {!review && !reviewError && <div className="ll-loading">Checking the event…</div>}
      {review && (
        <>
          <div className="srce-field">
            <label className="srce-label" htmlFor="srce-pub-title">Title for suppliers</label>
            <input id="srce-pub-title" className="srce-input srce-input--wide" value={title} maxLength={255} disabled={locked}
              placeholder="What suppliers see as the event title"
              onChange={(e) => { touched.current.add('title'); setSaveState(''); setTitle(e.target.value); }}
              onBlur={() => saveField('title')} />
            <span className="srce-mut srce-hint">Suppliers never see your event name.</span>
          </div>
          <div className="srce-field">
            <label className="srce-label" htmlFor="srce-pub-due">Offers due</label>
            <input id="srce-pub-due" className="srce-input" type="datetime-local" value={dueLocal} disabled={locked}
              onChange={(e) => { touched.current.add('due'); setSaveState(''); setDueLocal(e.target.value); }}
              onBlur={() => saveField('due')} />
            <span className="srce-mut srce-hint">In your time zone. Suppliers see it in theirs.</span>
          </div>
          <label className="srce-inline">
            <input type="checkbox" checked={shareSites} disabled={locked} onChange={(e) => setShareSites(e.target.checked)} />
            Share site names with suppliers
          </label>
          <div className="srce-mut">Currency: {review.currency}</div>

          <div className="srce-field">
            <span className="srce-label">Who receives the invitation</span>
            {suppliers.length === 0 && <div className="srce-mut">No suppliers selected yet.</div>}
            {suppliers.map((s) => (
              <div key={s.supplier_id} className="srce-contact">
                <span className="srce-strong srce-contact-name">{s.supplier_name}</span>
                <input className="srce-input" type="email" value={(contacts[s.supplier_id] || {}).email || ''} disabled={locked}
                  maxLength={255} placeholder="Email" aria-label={`Contact email for ${s.supplier_name}`}
                  onChange={(e) => setContact(s.supplier_id, 'email', e.target.value)}
                  onBlur={() => saveField(`contact:${s.supplier_id}`)} />
                <input className="srce-input" value={(contacts[s.supplier_id] || {}).name || ''} disabled={locked}
                  maxLength={255} placeholder="Name (optional)" aria-label={`Contact name for ${s.supplier_name}`}
                  onChange={(e) => setContact(s.supplier_id, 'name', e.target.value)}
                  onBlur={() => saveField(`contact:${s.supplier_id}`)} />
              </div>
            ))}
            {!review.email_enabled && (
              <span className="srce-mut srce-hint">Email is off. After you publish, you copy each link and send it yourself.</span>
            )}
          </div>

          {review.blocks.length > 0 && (
            <div>
              <span className="srce-label">Fix before publishing</span>
              <CheckList items={review.blocks} tone="block" names={names} />
            </div>
          )}
          {review.warnings.length > 0 && (
            <div>
              <span className="srce-label">Check these</span>
              <CheckList items={review.warnings} tone="warn" names={names} />
            </div>
          )}

          {preview && (
            <div className="srce-preview-box">
              <span className="srce-label">What suppliers will see</span>
              <EventHeader untitled="No title yet"
                event={{ ...preview.event, round: preview.event.round || 1, due_at: dueValid ? dueDate.toISOString() : null }} />
              <EventDetails event={preview.event} />
              {preview.parts && preview.parts.items.length > 0 && (
                <>
                  <div className="srce-mut srce-gap srce-row">
                    Page {preview.parts.page} of {preview.parts.total_pages} ({preview.parts.total.toLocaleString()} parts). Check every page before you publish.
                    <button type="button" className="srce-btn" disabled={preview.parts.page <= 1}
                      onClick={() => setPreviewPage((p) => Math.max(1, p - 1))}>Previous</button>
                    <button type="button" className="srce-btn" disabled={preview.parts.page >= preview.parts.total_pages}
                      onClick={() => setPreviewPage((p) => p + 1)}>Next</button>
                  </div>
                  <PartsTable items={preview.parts.items} />
                </>
              )}
            </div>
          )}

          {publishError && <div className="srce-error" role="alert">{publishError}</div>}
          {publishBlocks && publishBlocks.length > 0 && <CheckList items={publishBlocks} tone="block" names={names} />}
          <div className="srce-actions">
            <button type="button" className="srce-btn srce-btn--primary" onClick={publish}
              disabled={locked || blocked || missing.length > 0}>
              {publishing ? 'Publishing…' : 'Publish and invite suppliers'}
            </button>
            {!blocked && missing.length > 0 && <span className="srce-mut">{missing[0]}</span>}
            {blocked && <span className="srce-mut">Fix the items in red first.</span>}
          </div>
        </>
      )}
    </section>
  );
}
